import { Tooltip } from "@mui/material";
import React, { CSSProperties, FC, useEffect, useState } from "react";
import {
    FixMeLater,
    NumField,
    TextField,
    CheckField as CheckFieldType,
} from "src/types";
import DynamicColors from "src/utils/DynamicColors";
import "./CheckField.scss";

interface CheckFieldProps {
    scale: number;
    templateField: FixMeLater;
    value: {
        value: string | number | boolean;
        isOverride: boolean;
        documentField: NumField | TextField | CheckFieldType;
    };
    updateReturnDocument: (
        fieldType: string,
        documentNode: NumField | TextField | CheckFieldType,
        overrideValue: string | number | boolean,
    ) => void;
    isLocked?: boolean;
}

const CheckField: FC<CheckFieldProps> = ({
    scale,
    templateField,
    value,
    updateReturnDocument,
    isLocked,
}) => {
    const { position, size } = templateField?.screenBox || {};
    const {
        isEnabled,
        overrideState,
        tabIndex,
        checkFormatType,
        toolTip,
        fieldType,
        id,
    } = templateField;

    const [isChecked, setIsChecked] = useState<boolean>(Boolean(value?.value));

    useEffect(() => {
        // Update inputValue when value.value changes
        setIsChecked(Boolean(value?.value));
    }, [value]);

    const [isFocused, setIsFocused] = useState(false);

    const fontSize = Math.min(size?.width * scale, size?.height * scale);

    const handleCheckboxFocus = () => {
        setIsFocused(true);
    };

    const handleCheckboxBlur = () => {
        setIsFocused(false);
    };

    const handleCheckboxChange = () => {
        if (isLocked) return;
        if (!isChecked != Boolean(value.value)) {
            updateReturnDocument(
                "checkFields",
                value?.documentField,
                !isChecked,
            );
        }
        setIsChecked((prev) => !prev);
    };

    const dynamicTextFieldColor = DynamicColors.calculateDynamicTextFieldColor(
        isEnabled,
        overrideState,
        value?.isOverride,
    );
    const dynamicBackgroundColor =
        DynamicColors.calculateDynamicBackgroundColor(isEnabled, overrideState);
    // Dynamic styles
    const dynamicStyles: CSSProperties = {
        backgroundColor: dynamicBackgroundColor,
        color: isChecked ? dynamicTextFieldColor : "transparent",
        left: `${position?.left * scale}px`,
        top: `${position?.top * scale}px`,
        height: `${size?.height * scale}px`,
        width: `${size?.width * scale}px`,
        boxShadow: isFocused ? "0 0 0 2px rgba(0, 0, 255)" : "none",
    };

    const dynamicStylesDarkenOverlay: CSSProperties = {
        backgroundColor: isChecked ? dynamicTextFieldColor : "transparent",
    };

    const dynamicStylesXOverlay: CSSProperties = {
        fontSize: `${fontSize}px`,
    };

    return (
        <Tooltip title={toolTip} placement="top">
            <div className={"check-field-container"} style={dynamicStyles}>
                <input
                    className="checkbox-input"
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    readOnly={isLocked}
                    id={id + fieldType}
                    tabIndex={tabIndex}
                    onFocus={handleCheckboxFocus}
                    onBlur={handleCheckboxBlur}
                    style={{ cursor: isLocked ? "not-allowed" : "" }}
                />
                {checkFormatType === "DARKEN" && (
                    <label
                        className="darken-overlay"
                        style={dynamicStylesDarkenOverlay}
                    ></label>
                )}
                {checkFormatType === "X" && (
                    <label className="x-overlay" style={dynamicStylesXOverlay}>
                        X
                    </label>
                )}
                {checkFormatType === "CHECKMARK" && (
                    <label className="x-overlay" style={dynamicStylesXOverlay}>
                        ✓
                    </label>
                )}
            </div>
        </Tooltip>
    );
};

export default CheckField;
